/**
 * NotificationBell — bell icon with dropdown panel.
 *
 * Renders in the topbar. Shows unread count badge.
 * Dropdown lists recent notifications grouped by type,
 * with click-through to the relevant page.
 *
 * Role invitations are passive notifications that link to the Action Center
 * (/approvals) where the user can review details and Accept / Decline.
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { useNotifications } from '../../notifications/useNotifications';
import type { AppNotification, NotificationType } from '../../notifications/types';
import { mediaSrc } from '../../api/mediaUrl';
import styles from './NotificationBell.module.css';

export default function NotificationBell() {
  const { t } = useAppTranslation('chat');
  const navigate = useNavigate();
  const { grouped, unreadCount, markRead, markAllRead } = useNotifications();
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        panelRef.current &&
        e.target instanceof Node &&
        !panelRef.current.contains(e.target) &&
        btnRef.current &&
        !btnRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleNotifClick = useCallback(
    (notif: AppNotification) => {
      markRead(notif.id, notif.groupIds);
      if (notif.href) {
        navigate(notif.href);
      }
      setOpen(false);
    },
    [markRead, navigate]
  );

  const typeIcon = (type: NotificationType): string => {
    switch (type) {
      case 'chat_message': return '\uD83D\uDCAC'; // 💬
      case 'contact_request': return '\uD83D\uDC64'; // 👤
      case 'contact_accepted': return '\u2705'; // ✅
      case 'approval_needed': return '\uD83D\uDCCB'; // 📋
      case 'project_update': return '\uD83D\uDCC1'; // 📁
      case 'role_invitation': return '\uD83D\uDCE9'; // 📩
      case 'role_invitation_sent': return '\uD83D\uDCE4'; // 📤
      case 'role_invitation_response': return '\u2709\uFE0F'; // ✉️
      case 'system': return '\u2139\uFE0F'; // ℹ️
      default: return '\uD83D\uDD14'; // 🔔
    }
  };

  const formatTime = (ts: string): string => {
    try {
      const d = new Date(ts);
      const now = new Date();
      const diffMs = now.getTime() - d.getTime();
      const diffMin = Math.floor(diffMs / 60000);
      if (diffMin < 1) return t('notifications.justNow');
      if (diffMin < 60) return t('notifications.minutesAgo', { count: diffMin });
      const diffHrs = Math.floor(diffMin / 60);
      if (diffHrs < 24) return t('notifications.hoursAgo', { count: diffHrs });
      return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
    } catch {
      return '';
    }
  };

  return (
    <div className={styles.bellWrapper}>
      <button
        ref={btnRef}
        type="button"
        className={styles.bellButton}
        aria-label={t('notifications.title')}
        onClick={() => setOpen((v) => !v)}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unreadCount > 0 && (
          <span className={styles.bellBadge}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div ref={panelRef} className={styles.dropdown}>
          {/* Header */}
          <div className={styles.dropdownHeader}>
            <span className={styles.dropdownTitle}>{t('notifications.title')}</span>
            {unreadCount > 0 && (
              <button
                type="button"
                className={styles.markAllBtn}
                onClick={() => markAllRead()}
              >
                {t('notifications.markAllRead')}
              </button>
            )}
          </div>

          {/* List */}
          <div className={styles.notifList}>
            {grouped.length === 0 && (
              <div className={styles.emptyState}>{t('notifications.empty')}</div>
            )}

            {grouped.map((notif) => (
              <button
                key={notif.id}
                type="button"
                className={`${styles.notifItem} ${notif.isRead ? '' : styles.notifUnread}`}
                onClick={() => handleNotifClick(notif)}
              >
                {/* Avatar or icon (left side) */}
                <div className={styles.notifIcon}>
                  {notif.avatarUrl ? (
                    <img src={mediaSrc(notif.avatarUrl) ?? undefined} alt={notif.title} className={styles.notifAvatar} />
                  ) : (
                    <span className={styles.notifEmoji}>{typeIcon(notif.type)}</span>
                  )}
                </div>

                {/* Text content */}
                <div className={styles.notifContent}>
                  <div className={styles.notifTitle}>{notif.title}</div>
                  {notif.groupCount && notif.groupCount > 1 ? (
                    <div className={styles.notifBody}>
                      {t('notifications.groupedMessages', { count: notif.groupCount })}
                    </div>
                  ) : notif.body ? (
                    <div className={styles.notifBody}>{notif.body}</div>
                  ) : null}
                  <div className={styles.notifTime}>{formatTime(notif.timestamp)}</div>
                </div>

                {/* Unread dot */}
                {!notif.isRead && <span className={styles.unreadDot} />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
